/**
 * Reconstructs an editable Project from imported addon contents.
 *
 * Best-effort: we adopt the primary namespace, pull items/recipes defined under
 * it, resolve display names + guide pages from lang files, and reuse manifest
 * UUIDs where present. Everything is stored as BARE name-parts (Section 9.2) so
 * the imported project behaves exactly like a fresh one.
 */

import { nanoid } from "nanoid";
import type { GuideCategory, GuideEntry, Item, ItemRef, Language, Project, Recipe } from "../model/types";
import { PROJECT_FORMAT_VERSION } from "../model/types";
import { newProject } from "../model/defaults";
import type { AddonAnalysis } from "./analyze";
import type { AddonContents } from "./mcaddon";
import { localeFromPath, parseLang } from "./lang";
import { detectGuide, detectGuideNamespace, type DetectedEntry } from "./guideLang";

const LOCALE_LABELS: Record<string, string> = {
  en_US: "English (US)",
  en_GB: "English (UK)",
  es_ES: "Spanish (Spain)",
  es_MX: "Spanish (Mexico)",
  de_DE: "German",
  fr_FR: "French",
  fr_CA: "French (Canada)",
  pt_BR: "Portuguese (Brazil)",
  ru_RU: "Russian",
  ja_JP: "Japanese",
  ko_KR: "Korean",
  zh_CN: "Chinese (Simplified)",
  zh_TW: "Chinese (Traditional)",
};

function labelFor(code: string): string {
  return LOCALE_LABELS[code] ?? code;
}

/** Build a per-locale map for a lang key across all imported lang files. */
function collectPerLocale(
  contents: AddonContents,
  keyFor: (locale: string) => string,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { path, content } of contents.langFiles) {
    const locale = localeFromPath(path);
    const parsed = parseLang(content);
    const wanted = keyFor(locale);
    const found = parsed.lines.find((l) => l.key === wanted);
    if (found) out[locale] = found.value.replace(/\\n/g, "\n");
  }
  return out;
}

function refFrom(namespace: string, fullItem: string | undefined, tag = false): ItemRef {
  if (!fullItem) return { kind: "vanilla", id: "minecraft:air" };
  const idx = fullItem.indexOf(":");
  if (idx === -1) return { kind: "vanilla", id: `minecraft:${fullItem}`, ...(tag ? { tag } : {}) };
  const ns = fullItem.slice(0, idx);
  const name = fullItem.slice(idx + 1);
  return ns === namespace
    ? { kind: "project", id: name, ...(tag ? { tag } : {}) }
    : { kind: "vanilla", id: fullItem, ...(tag ? { tag } : {}) };
}

/**
 * Read one recipe ingredient / key slot into an ItemRef.
 *
 * Bedrock writes a slot as `{"item": "..."}`, as `{"tag": "..."}` for "any item
 * with this tag" (e.g. `minecraft:planks`), as a bare string, or as an array of
 * alternatives. Reading only `.item` silently turned every tag slot into
 * `minecraft:air`, so all four forms are handled here.
 */
function refFromSlot(namespace: string, slot: unknown): ItemRef {
  if (typeof slot === "string") return refFrom(namespace, slot);
  if (Array.isArray(slot)) {
    // Alternatives ("one of these"). We model a single ref, so keep the first
    // and let the rest go; the editor still shows a real item instead of air.
    return refFromSlot(namespace, slot[0]);
  }
  if (slot && typeof slot === "object") {
    const obj = slot as { item?: unknown; tag?: unknown };
    if (typeof obj.tag === "string") return refFrom(namespace, obj.tag, true);
    if (typeof obj.item === "string") return refFrom(namespace, obj.item);
  }
  return refFrom(namespace, undefined);
}

export interface ImportOptions {
  /** Override the detected namespace. */
  namespace?: string;
  /** Which name-part should be treated as the guide item. */
  guideItemId?: string;
}

export function projectFromAddon(
  contents: AddonContents,
  analysis: AddonAnalysis,
  options: ImportOptions = {},
): Project {
  const namespace = options.namespace ?? analysis.primaryNamespace ?? "importedpack";

  // Items under the chosen namespace.
  const nsItems = analysis.items.filter((i) => i.namespace === namespace);

  // Guess the guide item: explicit option → an item literally named "guide" →
  // first item → fall back to "guide".
  const guideItemId =
    options.guideItemId ??
    (nsItems.find((i) => i.name === "guide")?.name || nsItems[0]?.name || "guide");

  const items: Item[] = nsItems
    .filter((i) => i.name !== guideItemId)
    .map((i) => ({
      uuid: nanoid(),
      id: i.name,
      displayName: collectPerLocale(contents, () => `item.${namespace}:${i.name}`),
      category: "items",
    }));
  // Ensure at least an en_US display name exists.
  for (const it of items) {
    if (Object.keys(it.displayName).length === 0) it.displayName = { en_US: it.id };
  }

  const recipes: Recipe[] = analysis.recipes
    .filter((r) => r.namespace === namespace)
    .map((r): Recipe => {
      const body = findRecipeBody(contents, r.path);
      const shape = body?.kind ?? "shapeless";
      return {
        uuid: nanoid(),
        id: r.name,
        shape,
        tags: body?.tags ?? ["crafting_table"],
        ingredients: (body?.ingredients ?? []).map((slot) => refFromSlot(namespace, slot)),
        pattern: body?.pattern ?? ["X"],
        key: mapKey(namespace, body?.key),
        result: refFrom(namespace, r.resultItem),
        resultCount: body?.resultCount ?? 1,
      };
    });

  // Languages from lang files. en_US is the default/base locale, so it is
  // always present and listed first (the exporter also falls back to en_US).
  const discovered = [...new Set(analysis.langFiles.map((f) => f.locale))];
  const ordered = ["en_US", ...discovered.filter((c) => c !== "en_US")];
  const languages: Language[] = ordered.map((code) => ({ code, label: labelFor(code) }));

  // Retain the original lang files verbatim (concatenated per locale when a
  // locale appears in both BP and RP) so export MERGES into them rather than
  // replacing them.
  const importedLang: Record<string, string> = {};
  for (const f of contents.langFiles) {
    const locale = localeFromPath(f.path);
    importedLang[locale] = importedLang[locale] ? `${importedLang[locale]}\n${f.content}` : f.content;
  }

  // Guide item title (its display name) + the full category hierarchy. The
  // guide may live under a different namespace than the items, so detect it.
  const guideTitle = collectPerLocale(contents, () => `item.${namespace}:${guideItemId}`);
  const allKeys = contents.langFiles.flatMap((f) => parseLang(f.content).lines.map((l) => l.key));
  const guideNamespace = detectGuideNamespace(allKeys) ?? namespace;
  const { intro, categories } = reconstructGuide(contents, guideNamespace);

  // Reuse manifest UUIDs where available.
  const behavior = contents.manifests.find((m) => m.packType === "behavior");
  const resource = contents.manifests.find((m) => m.packType === "resource");

  const base = newProject(namespace);
  const packNameFromLang = collectPerLocale(contents, () => "pack.name").en_US;

  return {
    formatVersion: PROJECT_FORMAT_VERSION,
    meta: {
      name: packNameFromLang || behavior?.headerName || namespace,
      description: collectPerLocale(contents, () => "pack.description").en_US || "Imported guidebook add-on.",
      author: "",
    },
    namespace,
    guideItemId,
    languages,
    items,
    recipes: recipes.length ? recipes : base.recipes,
    guide: {
      title: Object.keys(guideTitle).length ? guideTitle : { en_US: "Guidebook" },
      intro: Object.keys(intro).length ? intro : base.guide.intro,
      // The imported pack already has its own give-on-join logic in its script,
      // which we don't regenerate, so default this off for imports.
      giveOnFirstJoin: false,
      categories: categories.length ? categories : base.guide.categories,
    },
    manifest: {
      ...base.manifest,
      behaviorHeaderUuid: behavior?.headerUuid ?? base.manifest.behaviorHeaderUuid,
      behaviorModuleUuid: behavior?.moduleUuids[0] ?? base.manifest.behaviorModuleUuid,
      resourceHeaderUuid: resource?.headerUuid ?? base.manifest.resourceHeaderUuid,
      resourceModuleUuid: resource?.moduleUuids[0] ?? base.manifest.resourceModuleUuid,
    },
    importedLang,
  };
}

interface RecipeBody {
  kind: "shapeless" | "shaped";
  tags: string[];
  /** Raw ingredient slots, as written in the recipe JSON (see `refFromSlot`). */
  ingredients?: unknown[];
  pattern?: string[];
  key?: Record<string, unknown>;
  resultCount: number;
}

function findRecipeBody(contents: AddonContents, path: string): RecipeBody | undefined {
  const file = contents.recipeFiles.find((f) => f.path === path);
  if (!file) return undefined;
  const json = file.json as any;
  const shapeless = json?.["minecraft:recipe_shapeless"];
  const shaped = json?.["minecraft:recipe_shaped"];
  if (shapeless) {
    return {
      kind: "shapeless",
      tags: Array.isArray(shapeless.tags) ? shapeless.tags : ["crafting_table"],
      ingredients: Array.isArray(shapeless.ingredients) ? shapeless.ingredients : [],
      resultCount: shapeless.result?.count ?? 1,
    };
  }
  if (shaped) {
    const key: Record<string, unknown> = {};
    for (const [k, v] of Object.entries<unknown>(shaped.key ?? {})) key[k] = v;
    return {
      kind: "shaped",
      tags: Array.isArray(shaped.tags) ? shaped.tags : ["crafting_table"],
      pattern: Array.isArray(shaped.pattern) ? shaped.pattern : ["X"],
      key,
      resultCount: shaped.result?.count ?? 1,
    };
  }
  return undefined;
}

function mapKey(namespace: string, key?: Record<string, unknown>): Record<string, ItemRef> {
  const out: Record<string, ItemRef> = {};
  for (const [k, v] of Object.entries(key ?? {})) out[k] = refFromSlot(namespace, v);
  return out;
}

/** Strip Bedrock formatting codes (§x) and trim, for deriving ids from titles. */
function stripFormatting(s: string): string {
  return s.replace(/§./g, "").trim();
}

function slugify(s: string): string {
  return stripFormatting(s)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Choose a stable bare name-part id for an imported entry: prefer a slug of its
 * title, fall back to the detector's suggestion, guarantee uniqueness.
 */
function entryId(detected: DetectedEntry, title: Record<string, string>, used: Set<string>): string {
  const fromTitle = slugify(title.en_US ?? Object.values(title)[0] ?? "");
  let base = /^[a-z0-9_]+$/.test(fromTitle) && fromTitle ? fromTitle : detected.suggestedId;
  if (!/^[a-z0-9_]+$/.test(base)) base = "entry";
  let id = base;
  let n = 2;
  while (used.has(id)) id = `${base}_${n++}`;
  used.add(id);
  return id;
}

/** Rebuild the full category → entry guide hierarchy from lang keys. */
function reconstructGuide(
  contents: AddonContents,
  namespace: string,
): { intro: Record<string, string>; categories: GuideCategory[] } {
  // A pack can ship the same locale in both BP and RP (e.g. an empty BP en_US
  // and the real RP en_US). Aggregate keys from every en_US file, in order.
  const enFiles = contents.langFiles.filter((f) => localeFromPath(f.path) === "en_US");
  const source = enFiles.length ? enFiles : contents.langFiles;
  const keys = source.flatMap((f) => parseLang(f.content).lines.map((l) => l.key));
  const detected = detectGuide(namespace, keys);

  const categories: GuideCategory[] = detected.categories.map((cat) => {
    const usedEntryIds = new Set<string>();
    const entries: GuideEntry[] = cat.entries.map((e) => {
      const title = collectPerLocale(contents, () => e.titleKey);
      const body = e.bodyKey ? collectPerLocale(contents, () => e.bodyKey!) : {};
      // Titles from numbered entries carry §l formatting; keep readable text.
      for (const k of Object.keys(title)) title[k] = stripFormatting(title[k]);
      return { uuid: nanoid(), id: entryId(e, title, usedEntryIds), title, body };
    });
    return {
      uuid: nanoid(),
      id: cat.id,
      title: cat.titleKey ? collectPerLocale(contents, () => cat.titleKey!) : { en_US: cat.id },
      body: cat.bodyKey ? collectPerLocale(contents, () => cat.bodyKey!) : {},
      entries,
    };
  });

  const intro = detected.homeBodyKey ? collectPerLocale(contents, () => detected.homeBodyKey!) : {};
  return { intro, categories };
}
